import { groupHierarchically, WithChildren } from '@upright/common/model/input/blanket/blanket-product/hierarchy-utils';
import { PopulatedBlanketProduct } from '@upright/common/model/input/blanket/blanket-product/types';
import { toEditablePiuProductStates, toPiuProductState } from '@upright/common/model/input/blanket/utils';
import {
  ResolvedWeight,
  resolveProductWeightsRecursive,
} from '@upright/common/model/input/companies/products/blanket-ratios';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { compact } from 'lodash';

// NOTE: this lives in its own module, not in `main-state.ts`, so that `build-piu-products-spec.ts`
// can require it. `yarn test-unit` runs mocha with `ts-node/register/transpile-only`, which resolves
// `.ts` but not `.tsx`; `main-state.ts` reaches `src/ui/integrations/appcues/index.tsx` through
// `../tracking`, and a spec importing it fails at load time and takes the whole unit suite down.

/**
 * PLT-5109: build the PIU product rows from a loaded blanket set.
 *
 * Editable (PIU) sets keep their stored relative `weight` as the revenue share, unchanged.
 *
 * Read-only (old "blanketti") sets are a hierarchy of group rows and leaf rows. Only the *leaf*
 * rows carry a mapping to the Upright taxonomy — they are the products the company is actually
 * assessed on. The group/parent rows are pure scaffolding (no taxonomy mapping); their disclosed
 * revenue is allocated *down* to their children via the canonical blanket weight-resolution logic
 * (`resolveProductWeightsRecursive` — the very same function the blanket product-set editor and the
 * publish-to-company-product-set flow use): an exact `relative` weight is kept as-is, otherwise the
 * parent's share is split among the children by their minor/medium/major class ratio (all-equal ⇒
 * even split), recursively until the leaves.
 *
 * So for the read-only view we render *only the leaf products*, each showing its canonical
 * allocated share (e.g. Smartphones 50.4 %, Laptops 4.0 %, Apple Pay 2.6 %, …; leaves sum to 100 %),
 * and drop the parent/group rows entirely — they are not assessable products and previously showed
 * either no chip or a misleading parent-level share. The products arrive flat (each carrying
 * `parentId`), so we first rebuild the hierarchy with `groupHierarchically`, resolve weights per
 * group, then keep the leaves (nodes with no children) and map each leaf's resolved weight back by
 * id.
 *
 * Also used by the Edit company dialog's Company details, which saves the figures on a draft of the
 * products as the server has them, the same round trip the editor makes (PLT-6277).
 */
export function buildPiuProducts(blanketProducts: PopulatedBlanketProduct[], readOnly: boolean): PiuProductState[] {
  if (!readOnly) {
    // Editable set: keep every node so the hierarchy is fully editable (DnD / add-child) and
    // round-trips through the whole-set save. Shared with the chat's edits and file save, which
    // rewrite the same set (PLT-7127).
    return toEditablePiuProductStates(blanketProducts);
  }

  const resolvedWeightById = new Map<number, number>();
  const leafProductIds = new Set<number>();
  // NOTE: don't use `ReturnType<typeof resolveProductWeightsRecursive>` here — for a generic
  // function, TypeScript instantiates `ReturnType` at the generic's *constraint*
  // (`Pick<BlanketProduct, 'weightType' | 'weight'>`), which has no `id`, so `node.id` fails
  // to typecheck. Spell out the concrete instantiation instead.
  const walkResolvedWeights = (nodes: Array<WithChildren<PopulatedBlanketProduct & ResolvedWeight>>) => {
    for (const node of nodes) {
      resolvedWeightById.set(node.id, node.resolvedWeight);
      if (node.children.length === 0) {
        leafProductIds.add(node.id);
      } else {
        walkResolvedWeights(node.children);
      }
    }
  };
  walkResolvedWeights(resolveProductWeightsRecursive(groupHierarchically(blanketProducts)));

  return compact(
    blanketProducts
      // Keep only genuine leaf PRODUCTS. Container / segment nodes carry no taxonomy mapping
      // (`taxonomyMapping == null`) — they are pure scaffolding, not assessable products — so a
      // childless empty segment would otherwise surface here as a bogus product row. Excluding
      // no-mapping nodes drops them entirely, which is the desired behavior. Weight resolution
      // above still runs over the full tree, so kept leaves keep their allocated share.
      .filter(product => leafProductIds.has(product.id) && product.taxonomyMapping !== null)
      .map(product => toPiuProductState(product, resolvedWeightById.get(product.id), true)),
  );
}
